/**
 * Board / Bench / Storage / Equipment (SCOPE_STAGE2 Punkt 6) + Verkauf.
 * Alle Mutationen validieren; Rueckgabe false = abgelehnt (UI zeigt Grund an).
 */
import { hexIndex, type HexPos } from '../sim/hex'
import type { StatKey, TeamSetup, UnitSetup } from '../sim/types'
import type { OwnedChampion, OwnedWeapon, RunState } from './types'
import { deployCap, sellValue, storageSize } from './economy'

export function boardChampions(state: RunState): OwnedChampion[] {
  return state.champions.filter((c) => c.hex !== null)
}

export function benchChampions(state: RunState): OwnedChampion[] {
  return state.champions.filter((c) => c.hex === null)
}

export function benchFull(state: RunState): boolean {
  return benchChampions(state).length >= state.config.bench.size
}

/** unequipped weapons (nicht von einem Champion referenziert) belegen Storage */
export function storageWeapons(state: RunState): OwnedWeapon[] {
  const equipped = new Set<number>()
  for (const c of state.champions) for (const uid of c.weaponUids) equipped.add(uid)
  return state.weapons.filter((w) => !equipped.has(w.uid))
}

export function storageFull(state: RunState): boolean {
  return storageWeapons(state).length >= storageSize(state.config)
}

export function championByUid(state: RunState, uid: number): OwnedChampion | undefined {
  return state.champions.find((c) => c.uid === uid)
}

export function weaponByUid(state: RunState, uid: number): OwnedWeapon | undefined {
  return state.weapons.find((w) => w.uid === uid)
}

function isOwnHalfHex(state: RunState, hex: HexPos): boolean {
  const g = state.config.grid
  return hex.col >= 0 && hex.col < g.cols && hex.row >= g.rowsPerSide && hex.row < g.rowsPerSide * 2
}

export function fractureAt(state: RunState, hex: HexPos): boolean {
  return state.fractures.some((f) => f.col === hex.col && f.row === hex.row)
}

function occupantAt(state: RunState, hex: HexPos): OwnedChampion | undefined {
  return state.champions.find((c) => c.hex && c.hex.col === hex.col && c.hex.row === hex.row)
}

/** Unit aufs Board setzen/verschieben. Fractures blocken NUR Deployment (docs/01). */
export function placeChampion(state: RunState, uid: number, hex: HexPos): boolean {
  const c = championByUid(state, uid)
  if (!c) return false
  if (!isOwnHalfHex(state, hex)) return false
  if (fractureAt(state, hex)) return false
  if (occupantAt(state, hex)) return false
  if (c.hex === null && boardChampions(state).length >= deployCap(state.round, state.config)) return false
  c.hex = { ...hex }
  return true
}

export function moveToBench(state: RunState, uid: number): boolean {
  const c = championByUid(state, uid)
  if (!c || c.hex === null) return false
  if (benchFull(state)) return false
  c.hex = null
  return true
}

function handsUsed(state: RunState, c: OwnedChampion): number {
  let h = 0
  for (const uid of c.weaponUids) {
    const w = weaponByUid(state, uid)
    if (w) h += state.config.weapons[w.weapon]!.hands
  }
  return h
}

/** Waffe aus dem Storage ausruesten (Hand-Slots max 2, docs/05) */
export function equipWeapon(state: RunState, champUid: number, weaponUid: number): boolean {
  const c = championByUid(state, champUid)
  const w = weaponByUid(state, weaponUid)
  if (!c || !w) return false
  if (c.weaponUids.includes(weaponUid)) return false
  // weapon must be unequipped (storage)
  for (const other of state.champions) {
    if (other.weaponUids.includes(weaponUid)) return false
  }
  if (handsUsed(state, c) + state.config.weapons[w.weapon]!.hands > 2) return false
  c.weaponUids.push(weaponUid)
  return true
}

/** Waffe ablegen -> Storage (nur wenn Platz) */
export function unequipWeapon(state: RunState, champUid: number, weaponUid: number): boolean {
  const c = championByUid(state, champUid)
  if (!c || !c.weaponUids.includes(weaponUid)) return false
  if (storageFull(state)) return false
  c.weaponUids = c.weaponUids.filter((u) => u !== weaponUid)
  return true
}

/**
 * Freigewordene (unequipped) Waffen in den Storage uebernehmen; passt der Rest
 * nicht mehr hinein, wird NUR der Overflow mitverkauft (A-CC17). Reihenfolge: uid asc.
 */
export function absorbFreedWeapons(state: RunState, freedUids: number[]): void {
  const sorted = [...freedUids].sort((a, b) => a - b)
  for (const wUid of sorted) {
    // Waffe ist bereits unequipped und zaehlt gegen den Storage — nur bei Ueberlauf verkaufen
    if (storageWeapons(state).length > storageSize(state.config)) {
      const w = weaponByUid(state, wUid)
      if (w) {
        state.gold += sellValue(w.buyPrice, w.boughtRound, state.round, state.config)
        state.weapons = state.weapons.filter((x) => x.uid !== wUid)
      }
    }
  }
}

/**
 * Champion verkaufen. Ausgeruestete Waffen wandern in den Storage;
 * nur was nicht mehr hineinpasst, wird mitverkauft (A-CC17).
 */
export function sellChampion(state: RunState, uid: number): boolean {
  const c = championByUid(state, uid)
  if (!c) return false
  state.gold += sellValue(c.buyPrice, c.boughtRound, state.round, state.config)
  const weaponUids = [...c.weaponUids]
  c.weaponUids = []
  state.champions = state.champions.filter((x) => x.uid !== uid)
  absorbFreedWeapons(state, weaponUids)
  return true
}

export function sellWeapon(state: RunState, uid: number): boolean {
  const w = weaponByUid(state, uid)
  if (!w) return false
  for (const c of state.champions) {
    c.weaponUids = c.weaponUids.filter((u) => u !== uid)
  }
  state.gold += sellValue(w.buyPrice, w.boughtRound, state.round, state.config)
  state.weapons = state.weapons.filter((x) => x.uid !== uid)
  return true
}

/** kombinierter finaler Stat-Prozentsatz: Tier-Skalierung (A6, multiplikativ) + Passives (additiv) */
export function unitStatPct(state: RunState, c: OwnedChampion): Partial<Record<StatKey, number>> {
  const cfg = state.config
  let tierPct = 100
  if (c.tier >= 2) tierPct = Math.floor((tierPct * cfg.upgrade.tier2StatMultPct) / 100)
  if (c.tier >= 3) tierPct = Math.floor((tierPct * cfg.upgrade.tier3StatMultPct) / 100)
  // team passives: additive percent per stat
  const add: Record<StatKey, number> = { hp: 0, ad: 0, ap: 0, armor: 0, resist: 0, attackSpeed: 0 }
  for (const key of state.passives) {
    const p = cfg.passives[key]
    if (p) add[p.stat] += p.pct
  }
  const out: Partial<Record<StatKey, number>> = {}
  // Tier skaliert HP/AD/AP (A6); Passives wirken auf ihren Stat additiv
  out.hp = Math.floor((tierPct * (100 + add.hp)) / 100)
  out.ad = Math.floor((tierPct * (100 + add.ad)) / 100)
  out.ap = Math.floor((tierPct * (100 + add.ap)) / 100)
  out.armor = 100 + add.armor
  out.resist = 100 + add.resist
  out.attackSpeed = 100 + add.attackSpeed
  return out
}

/** Board -> TeamSetup fuer simulate() (Team A = eigene Haelfte unten) */
export function buildPlayerTeam(state: RunState): TeamSetup {
  const units: UnitSetup[] = []
  // deterministic order: by uid (stable deployment index)
  const placed = boardChampions(state).sort((a, b) => a.uid - b.uid)
  for (const c of placed) {
    const weaponKeys: string[] = []
    const weaponTiers: number[] = []
    for (const uid of c.weaponUids) {
      const w = weaponByUid(state, uid)
      if (w) {
        weaponKeys.push(w.weapon)
        weaponTiers.push(w.tier)
      }
    }
    units.push({
      champ: c.champ,
      hex: { ...c.hex! },
      weapons: weaponKeys,
      weaponTiers,
      statPct: unitStatPct(state, c),
      tier: c.tier,
    })
  }
  return units
}

/** Occupancy-Karte fuers Board-Editing (Fractures + Units) */
export function blockedDeployHexes(state: RunState): HexPos[] {
  return state.fractures.map((f) => ({ col: f.col, row: f.row }))
}

export function boardOccupancy(state: RunState): Map<number, OwnedChampion> {
  const map = new Map<number, OwnedChampion>()
  for (const c of state.champions) {
    if (c.hex) map.set(hexIndex(c.hex, state.config.grid.cols), c)
  }
  return map
}
